import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { useSearchParams } from 'react-router-dom'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { UncategorizedTransactionBanner } from '../shared/UncategorizedTransactionBanner'
import { isEmpty } from 'lodash'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import {
  useEndDateForFinancialInsights,
  useReselector,
} from '@/utils/sharedHooks'
import { selectActivePlaidFinancialAccounts } from '@/selectors/financeSelectors'

import { Alert, Card, Icon, Link, Text, Loader } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import { faChartLine } from '@fortawesome/pro-regular-svg-icons'
import { InsightsTrendGraph } from './InsightsTrendGraph'
import {
  INSIGHTS_TYPE,
  getInsightsDateRange,
  processDataForChart,
} from '../shared/utils'
import { fetchInsightsBarchartData } from '../shared/financeActions'
import { ProfitLossInsights } from './profit-loss-insights'
import {
  InsightsDatePicker,
  useInsightsDatePicker,
} from '../shared/InsightsDatePicker'
import { InsightOverviewCards } from './insight-overview-card'

export const PracticeOverview = () => {
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()
  const track = useAnalyticsTrack()
  const [trendData, setTrendData] = useState<
    { [key: string]: string | number | undefined }[]
  >([])
  const linkedPlaidAccounts = useReselector(selectActivePlaidFinancialAccounts)
  const hasLinkedPlaidAccounts = !isEmpty(linkedPlaidAccounts)

  const [searchParams] = useSearchParams()

  const { monthParam, yearParam } = useMemo(() => {
    return {
      monthParam: searchParams.get('month'),
      yearParam: searchParams.get('year'),
    }
  }, [searchParams])

  const { yearFilter, monthFilter, inputDate, setYearFilter, setMonthFilter } =
    useInsightsDatePicker(monthParam, yearParam)

  const currentDate = useMemo(() => {
    return inputDate
      ? DateTime.fromFormat(inputDate, DATE_FORMATS_LUXON.INPUT)
      : DateTime.now()
  }, [inputDate])

  const endDate = useEndDateForFinancialInsights(currentDate)
  const dateRange = useMemo(() => getInsightsDateRange(endDate), [endDate])

  useEffect(() => {
    pageView('practice insights')
  }, [pageView])

  useEffect(() => {
    dispatch(fetchFinancialAccountsIfNeeded())
  }, [dispatch])

  useEffect(() => {
    const fetchTrendData = async () => {
      const [startDate, endDate] = dateRange
      const fetchedData = await fetchInsightsBarchartData(
        INSIGHTS_TYPE.PROFIT,
        startDate,
        endDate
      )(dispatch)

      if (fetchedData) {
        const chartData = processDataForChart(
          fetchedData,
          dateRange,
          INSIGHTS_TYPE.PROFIT
        )
        setTrendData(chartData)
      }
    }
    fetchTrendData()
  }, [dispatch, dateRange])

  const monthDateRange = useMemo(() => {
    if (yearFilter && monthFilter) {
      const month = parseInt(monthFilter) + 1
      const end = DateTime.fromObject(
        { year: parseInt(yearFilter), month },
        { zone: 'utc' }
      ).endOf('month')
      const start = end.startOf('month')
      return [
        start.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
        end.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
      ]
    }
    return ['', '']
  }, [yearFilter, monthFilter])

  const dateRangeString = useMemo(() => {
    if (!dateRange) return ''
    const [startDate, endDate] = dateRange
    const start = DateTime.fromFormat(
      startDate,
      DATE_FORMATS_LUXON.TIMESTAMP
    ).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)
    const end = DateTime.fromFormat(
      endDate,
      DATE_FORMATS_LUXON.TIMESTAMP
    ).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)
    return `Last 12 months (${start} - ${end})`
  }, [dateRange])

  const handleMonthFilterChangeWithTrack = useCallback(
    (month: string) => {
      track('clicked month filter', {
        month,
        year: yearFilter ?? null,
        screen: 'practice insights',
      })
      setMonthFilter(month)
    },
    [track, yearFilter, setMonthFilter]
  )

  const handleYearFilterChangeWithTrack = useCallback(
    (year: number) => {
      track('clicked year filter', {
        year,
        month: monthFilter ?? null,
        screen: 'practice insights',
      })
      setYearFilter(year.toString())
    },
    [track, monthFilter, setYearFilter]
  )

  const [trendDataLoading, setTrendDataLoading] = useState(true)
  const [profitLossLoading, setProfitLossLoading] = useState(true)

  useEffect(() => {
    const trendTimeout = setTimeout(() => {
      setTrendDataLoading(false)
    }, 5000)

    const profitLossTimeout = setTimeout(() => {
      setProfitLossLoading(false)
    }, 5000)

    return () => {
      clearTimeout(trendTimeout)
      clearTimeout(profitLossTimeout)
    }
  }, [])

  useEffect(() => {
    if (trendData && trendData.length > 0) {
      setTrendDataLoading(false)
    }
  }, [trendData])

  useEffect(() => {
    if (monthDateRange[0] && monthDateRange[1]) {
      setProfitLossLoading(false)
    }
  }, [monthDateRange])

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <Text variant={TypographyStyles.H1}>Practice Insights</Text>
        <InsightsDatePicker
          monthFilter={monthFilter}
          yearFilter={yearFilter}
          handleMonthFilterChange={handleMonthFilterChangeWithTrack}
          handleYearFilterChange={(year) =>
            handleYearFilterChangeWithTrack(parseInt(year))
          }
          insightsType={INSIGHTS_TYPE.PROFIT}
        />
      </div>

      {!hasLinkedPlaidAccounts && (
        <Alert variant="warning">
          <Text variant={TypographyStyles.BODY_MD}>
            You do not have a connected bank account.{' '}
            <Link href="/accounts#connected-institutions" newPage={false}>
              Link Business Account
            </Link>
          </Text>
        </Alert>
      )}

      <UncategorizedTransactionBanner
        inputDate={inputDate}
        practiceInsightsPage
      />

      <Card>
        <div className="flex flex-row items-center gap-3 mb-4">
          <Icon
            backgroundStyle="rounded"
            backgroundColor="black"
            size="sm"
            icon={faChartLine}
            color="text-neutral-0"
            backgroundClassName="hidden sm:flex"
          />
          <div>
            <Text variant={TypographyStyles.H1}>Practice Overview</Text>
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              Birds eye view of your practice finances
            </Text>
          </div>
        </div>
        <div className="pb-8">
          {yearFilter !== undefined && monthFilter !== undefined ? (
            <InsightOverviewCards
              year={parseInt(yearFilter)}
              month={parseInt(monthFilter) + 1}
            />
          ) : (
            <Loader active />
          )}
        </div>

        <div className="flex flex-col justify-between max-w-[1200px] gap-2">
          <Text className="w-full" variant={TypographyStyles.LABEL_MD}>
            Practice Finances Over Time
          </Text>
          <Text
            className="w-full"
            variant={TypographyStyles.BODY_SM}
            color={TextColors.MUTED}
          >
            {dateRangeString}
          </Text>
        </div>

        <div className="py-8">
          {!trendDataLoading ? (
            <InsightsTrendGraph data={trendData} />
          ) : (
            <Loader active />
          )}
        </div>
      </Card>

      {!profitLossLoading && monthDateRange[0] && monthDateRange[1] ? (
        <ProfitLossInsights
          startDate={monthDateRange[0]}
          endDate={monthDateRange[1]}
        />
      ) : (
        <Loader active />
      )}
    </div>
  )
}
